import { type PrimitiveAtom, type useStore } from 'jotai';

import { spotlightOpenAtom } from '@frontend/atoms';
import { assertModePath, prefixesOf, toPath, toSegments } from '@frontend/utils/modePath';

import {
  spotlightActionsAtom,
  spotlightDefaultModeAtom,
  spotlightGroupsAtom,
  spotlightModeDataAtom,
  spotlightModesAtom,
  spotlightModeStackAtom,
  spotlightTypesAtom,
} from './atoms';
import {
  type TAction,
  type TActionInput,
  type TGroup,
  type TMode,
  type TModePath,
  type TModePathInput,
  type TModePathResolver,
  type TSpotlightAPI,
  type TType,
} from './types';

/** The ambient jotai store handle (`useStore()`), the only thing the API talks to. */
type SpotlightStore = ReturnType<typeof useStore>;

/** True only in a development build. Read at call time so it honours a runtime env stub. */
const isDev = () => process.env.NODE_ENV === 'development';

/** A conflicting registration is a bug: throw in dev so it's caught, warn in prod so nothing crashes. */
function reportConflict(message: string): void {
  if (isDev()) throw new Error(message);
  console.warn(message);
}

/** A benign-but-notable event: warn in dev only, never in prod (unlike a conflict). */
function devWarn(message: string): void {
  if (isDev()) console.warn(message);
}

/** Collision-free identity of an action within its mode (only for keyed actions). */
const actionId = (mode: TModePath, key: string) => JSON.stringify([mode, key]);

/**
 * Register keyed items (types/groups/modes) into a record atom. A key collision is
 * a conflict (throws in dev / warns + keeps the existing entry in prod). Cleanup
 * removes only the keys this call still owns (a later override isn't clobbered).
 */
function registerRecordStrict<T>(
  store: SpotlightStore,
  recordAtom: PrimitiveAtom<Record<string, T>>,
  kind: string,
  record: Record<string, T>
): () => void {
  // Track only the keys THIS call actually inserted. A key rejected as a duplicate is
  // owned by an earlier registration, so this call's cleanup must not remove it.
  const inserted: string[] = [];

  store.set(recordAtom, prev => {
    const next = { ...prev };
    for (const key of Object.keys(record)) {
      if (next[key] !== undefined) {
        reportConflict(`[Spotlight] duplicate ${kind} "${key}" — registrations must be unique`);
        continue;
      }
      next[key] = record[key];
      inserted.push(key);
    }
    // Nothing inserted (empty record, or all duplicates) → keep the same reference.
    return inserted.length ? next : prev;
  });

  return () => {
    if (inserted.length === 0) return;
    const insertedSet = new Set(inserted);
    store.set(recordAtom, prev => {
      const next: Record<string, T> = {};
      let removed = false;
      for (const key of Object.keys(prev)) {
        // Drop only the keys this call inserted and still owns; rebuild without `delete`.
        if (insertedSet.has(key) && prev[key] === record[key]) {
          removed = true;
          continue;
        }
        next[key] = prev[key];
      }
      // Return the same reference when nothing changed so jotai doesn't notify.
      return removed ? next : prev;
    });
  };
}

/**
 * Build the imperative Spotlight API bound to a jotai store. Registration methods
 * union into the registry atoms and return a cleanup that undoes exactly what they
 * added; mode ops mutate the path stack / data map.
 */
export function createSpotlightApi(store: SpotlightStore): TSpotlightAPI {
  // Monotonic per-frame push id, unique for the life of this api instance.
  let pushSeq = 0;

  return {
    open: () => store.set(spotlightOpenAtom, true),
    close: () => store.set(spotlightOpenAtom, false),
    toggle: () => store.set(spotlightOpenAtom, open => !open),

    addActions: (record: Record<TModePath, TActionInput[]>) => {
      const additions: TAction[] = [];
      for (const [pathKey, actions] of Object.entries(record)) {
        const mode = assertModePath(pathKey);
        for (const action of actions) additions.push({ ...action, mode });
      }

      let added: TAction[] = [];
      store.set(spotlightActionsAtom, prev => {
        const existing = new Set(
          prev.filter(a => a.key !== undefined).map(a => actionId(a.mode, a.key as string))
        );
        const batch = new Set<string>();
        added = additions.filter(a => {
          if (a.key === undefined) return true;
          const id = actionId(a.mode, a.key);
          if (existing.has(id) || batch.has(id)) {
            reportConflict(`[Spotlight] duplicate action key "${a.key}" for mode "${a.mode}"`);
            return false;
          }
          batch.add(id);
          return true;
        });
        // Same reference when nothing was added (empty/all-duplicate batch) → no spurious notify.
        return added.length ? [...prev, ...added] : prev;
      });

      return () => {
        if (added.length === 0) return;
        const addedSet = new Set(added);
        store.set(spotlightActionsAtom, prev => {
          const next = prev.filter(action => !addedSet.has(action));
          // Same reference when nothing was removed → no spurious jotai notify.
          return next.length === prev.length ? prev : next;
        });
      };
    },

    registerType: (types: Record<string, TType>) =>
      registerRecordStrict(store, spotlightTypesAtom, 'type', types),

    registerGroup: (groups: Record<string, TGroup>) =>
      registerRecordStrict(store, spotlightGroupsAtom, 'group', groups),

    registerModes: (modes: Record<TModePath, TMode>) => {
      const validated: Record<TModePath, TMode> = {};
      for (const [path, mode] of Object.entries(modes)) validated[assertModePath(path)] = mode;
      return registerRecordStrict(store, spotlightModesAtom, 'mode', validated);
    },

    registerModeData: (data: Record<TModePath, unknown>) => {
      const validated: Record<TModePath, unknown> = {};
      for (const [path, value] of Object.entries(data)) validated[assertModePath(path)] = value;
      return registerRecordStrict(store, spotlightModeDataAtom, 'mode data', validated);
    },

    updateModeData: update => {
      const prev = store.get(spotlightModeDataAtom);
      const next = update(prev);
      for (const key of Object.keys(next)) assertModePath(key);
      store.set(spotlightModeDataAtom, next);
      return () => {
        // Restore only if still ours (a later update isn't clobbered).
        if (store.get(spotlightModeDataAtom) === next) {
          store.set(spotlightModeDataAtom, prev);
        }
      };
    },

    pushMode: (input: TModePathInput | TModePathResolver) => {
      let index = -1;
      let pushedId: number | null = null;
      store.set(spotlightModeStackAtom, prev => {
        const top = prev[prev.length - 1]?.path || '/';
        const resolved = typeof input === 'function' ? input(top, toSegments(top)) : input;
        const newPath = toPath(resolved);

        if (newPath === top) {
          devWarn(`[Spotlight] pushed mode "${newPath}" onto itself`);
          index = prev.length;
          pushedId = ++pushSeq;
          return [...prev, { path: newPath, id: pushedId }];
        }
        if (prev.some(frame => frame.path === newPath)) {
          reportConflict(`[Spotlight] mode "${newPath}" is already in the stack`);
          return prev;
        }
        index = prev.length;
        pushedId = ++pushSeq;
        return [...prev, { path: newPath, id: pushedId }];
      });

      // Truncate back to (and including) the frame this call pushed, matched by its unique
      // push id: a same-path frame pushed after an intervening pop/setMode carries a
      // different id, so a stale disposer safely no-ops instead of slicing that frame.
      return () => {
        if (index < 0 || pushedId === null) return;
        store.set(spotlightModeStackAtom, prev => {
          if (prev[index]?.id !== pushedId) return prev;
          if (prev.length - index > 1) {
            devWarn(
              '[Spotlight] mode stack truncation occurred — frames above this one were popped'
            );
          }
          return prev.slice(0, index);
        });
      };
    },

    popMode: (n = 1) => {
      store.set(spotlightModeStackAtom, prev => {
        // Clamp both ends so a negative `n` can't overshoot `prev.length` and copy the whole array.
        const count = Math.min(prev.length, Math.max(0, prev.length - n));
        // Same reference when nothing is removed → no spurious jotai notify.
        return count === prev.length ? prev : prev.slice(0, count);
      });
    },

    setMode: (input: TModePathInput) => {
      const path = toPath(input);
      // Seed one frame per ancestor (root dropped ⇒ empty stack for "/"), so the
      // stack mirrors the path's nesting and Backspace/popMode step up one level
      // at a time instead of collapsing straight to root.
      store.set(
        spotlightModeStackAtom,
        prefixesOf(path)
          .slice(1)
          .map(framePath => ({ path: framePath, id: ++pushSeq }))
      );
    },

    setDefaultMode: (input: TModePathInput) => {
      const next = toPath(input);
      const prev = store.get(spotlightDefaultModeAtom);
      store.set(spotlightDefaultModeAtom, next);
      return () => {
        // Restore only if still ours (a later setter isn't clobbered).
        if (store.get(spotlightDefaultModeAtom) === next) {
          store.set(spotlightDefaultModeAtom, prev);
        }
      };
    },
  };
}
